import { ReactNode, use, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import { getClosestTimeFrame, hasAllDayTimeFrame } from 'shared/utils/timeframe-utils';

import AcousticConfiguration from 'features/residents/models/configurations/acoustic-configuration';
import AudioConfiguration from 'features/residents/models/configurations/audio-configuration';
import BaseAlarmOnlyScenarioConfiguration from 'features/residents/models/configurations/base-alarm-only-scenario-configuration';
import BaseAlarmScenarioConfiguration from 'features/residents/models/configurations/base-alarm-scenario-configuration';
import BaseSettingScenarioConfiguration from 'features/residents/models/configurations/base-setting-scenario-configuration';
import TemporaryAcousticScenarioConfiguration from 'features/residents/models/configurations/temporary-acoustic-configuration';
import VideoConfiguration from 'features/residents/models/configurations/video-configuration';
import WanderingDetectionConfiguration from 'features/residents/models/configurations/wandering-detection-configuration';
import ResidentSettingsProviderContext from 'features/residents/resident-settings/providers/contexts/resident-settings-provider-context';

type RowConfigurationAccordion = {
  expandScenario: boolean;
  toggleExpandAndActivateScenario: (checked: boolean) => void;
  toggleExpandScenario: () => void;
  renderScenarioRowSubTitleTimeFrame: () => ReactNode;
  scenarioRowSubTitleAlarmTime: string;
  scenarioRowTitleAlarmIndicator: string;
  getAccordionSummeryClassNames: string;
};

export type RowConfigurationAccordionProps = {
  index: number;
  subtitlePrefix: string;
  organizationUnitScenarioIsActive: boolean;
  temporaryAcousticConfiguration?: TemporaryAcousticScenarioConfiguration;
  showAlarmInfo: boolean;
  alarmTimeOut?: number | null;
  isAlarmActive?: boolean;
  isStatusActive?: boolean;
  configuration:
    | BaseAlarmScenarioConfiguration
    | BaseAlarmOnlyScenarioConfiguration
    | BaseSettingScenarioConfiguration
    | VideoConfiguration
    | AudioConfiguration
    | AcousticConfiguration
    | WanderingDetectionConfiguration;
};

const useRowConfigurationAccordion = ({
  index,
  subtitlePrefix,
  organizationUnitScenarioIsActive,
  temporaryAcousticConfiguration,
  configuration,
  showAlarmInfo,
  alarmTimeOut,
  isAlarmActive,
  isStatusActive,
}: Readonly<RowConfigurationAccordionProps>): RowConfigurationAccordion => {
  const { t } = useTranslation('residentsSettings');

  const {
    detailEditState: { viewingMode, residentSettingEditForm, currentResidentSetting },
  } = use(ResidentSettingsProviderContext);

  const [expandScenario, setExpandScenario] = useState<boolean>(false);

  const toggleExpendAndActivateScenario = (checked: boolean) => {
    if (checked || residentSettingEditForm.formState.isValid) setExpandScenario(checked);
    residentSettingEditForm.setValue(`organisationUnitScenarios.${index}.isActive`, checked, { shouldDirty: true });
  };

  const toggleExpendScenario = () => {
    if (organizationUnitScenarioIsActive || expandScenario) {
      setExpandScenario(!expandScenario);
    }
  };

  const isSpecializedTimeFrameConfig = (config: any): config is AcousticConfiguration | WanderingDetectionConfiguration => {
    return !!(config?.acousticTimeFrameConfigurations || config?.wanderingDetectionTimeFrameConfigurations);
  };

  const specializedTimeFrames =
    (configuration as AcousticConfiguration)?.acousticTimeFrameConfigurations ??
    (configuration as WanderingDetectionConfiguration)?.wanderingDetectionTimeFrameConfigurations;

  const scenarioActiveAllDay = specializedTimeFrames
    ? hasAllDayTimeFrame(specializedTimeFrames.map((item) => item.timeFrame))
    : ((configuration as BaseSettingScenarioConfiguration)?.activeAllDay ??
      (configuration as BaseAlarmScenarioConfiguration)?.sendAlarmAllDay ??
      false);

  const timeFrames = isSpecializedTimeFrameConfig(configuration)
    ? (specializedTimeFrames?.map((item) => item.timeFrame) ?? null)
    : configuration.timeFrames;

  const getAccordionSummeryClassNames = useMemo(() => {
    const classNames = ['configuration-row-container'];

    if (!organizationUnitScenarioIsActive) return 'configuration-row-container';

    classNames.push('scenario-active');

    if (scenarioActiveAllDay || temporaryAcousticConfiguration || (timeFrames && getClosestTimeFrame(timeFrames).isInTimeFrame)) {
      classNames.push('is-in-timeframe');
    }

    if (isAlarmActive) {
      classNames.push('alarm-active');
    }

    if (currentResidentSetting?.scenariosMuted) {
      classNames.push('resident-absent');
    }

    return classNames.join(' ');
  }, [
    organizationUnitScenarioIsActive,
    scenarioActiveAllDay,
    temporaryAcousticConfiguration,
    timeFrames,
    isAlarmActive,
    currentResidentSetting?.scenariosMuted,
  ]);

  const scenarioRowTitleAlarmIndicator = useMemo(() => {
    if (!organizationUnitScenarioIsActive) {
      return t('scenarioConfiguration.disabled');
    }

    if (!showAlarmInfo) {
      return '';
    }

    const alarmIndicatorTextKey = isAlarmActive ? 'alarm' : 'noAlarm';
    return t(`scenarioConfiguration.${alarmIndicatorTextKey}`);
  }, [showAlarmInfo, organizationUnitScenarioIsActive, isAlarmActive, t]);

  const scenarioRowSubTitleAlarmTime = useMemo(() => {
    if (alarmTimeOut === null || alarmTimeOut === 0 || alarmTimeOut === undefined) {
      return t('scenarioConfiguration.alarmTimeNow');
    }

    if (alarmTimeOut === -1) {
      return t('scenarioConfiguration.alarmIntentionOutOfBed');
    }

    const hours = Math.floor(alarmTimeOut / 3600);
    const minutes = Math.round((alarmTimeOut % 3600) / 60);

    if (minutes > 0 && hours === 0) {
      return t('scenarioConfiguration.afterMinutes', { minutes });
    }

    if (minutes === 0 && hours > 0) {
      return t('scenarioConfiguration.afterHours', { hours });
    }

    return t('scenarioConfiguration.afterHoursMinutes', { hours, minutes });
  }, [alarmTimeOut, t]);

  const renderScenarioRowSubTitleTimeFrame = (): ReactNode => {
    if (temporaryAcousticConfiguration) {
      const formattedTimeUntil = moment(temporaryAcousticConfiguration.validUntil).format('HH:mm');
      return (
        <Typography variant="body2" className="timeframe-text">
          {t('scenarioConfiguration.scenarioTemporaryActiveUntil', { time: formattedTimeUntil })}
        </Typography>
      );
    }

    if (isStatusActive && !isAlarmActive) return null;
    if (isStatusActive === undefined && !organizationUnitScenarioIsActive) return null;

    if (scenarioActiveAllDay) {
      return (
        <Typography variant="body2" className="timeframe-text">
          {subtitlePrefix}
        </Typography>
      );
    }

    if (timeFrames == null) return null;

    const closestTimeFrame = getClosestTimeFrame(timeFrames);
    if (!closestTimeFrame.formattedTimeFrameText) return null;

    const translationKey = closestTimeFrame.isInTimeFrame ? 'scenarioActiveUntil' : 'scenarioActiveFrom';
    const translationText = t(`scenarioConfiguration.${translationKey}`, { time: closestTimeFrame.formattedTimeFrameText });
    const text = `${subtitlePrefix} ${translationText}`;

    return (
      <Typography variant="body2" className={`timeframe-text ${!closestTimeFrame.isInTimeFrame ? 'not-in-timeframe' : ''}`}>
        {text}
      </Typography>
    );
  };

  useEffect(() => {
    if ((viewingMode === 'viewing' && !organizationUnitScenarioIsActive && expandScenario) || !currentResidentSetting) {
      setExpandScenario(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingMode, currentResidentSetting]);

  useEffect(() => {
    const errors = residentSettingEditForm.formState.errors.organisationUnitScenarios;
    if (errors?.[index] !== undefined && !expandScenario) {
      setExpandScenario(true);
    }
  }, [residentSettingEditForm.formState, index]);

  return {
    expandScenario,
    toggleExpandAndActivateScenario: toggleExpendAndActivateScenario,
    toggleExpandScenario: toggleExpendScenario,
    renderScenarioRowSubTitleTimeFrame,
    scenarioRowSubTitleAlarmTime,
    scenarioRowTitleAlarmIndicator,
    getAccordionSummeryClassNames,
  };
};

export default useRowConfigurationAccordion;
